'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { getPageBackgroundColor } from '@/lib/pageColors';

/**
 * ThemeColorUpdater - Dynamically updates the theme-color meta tag on route changes
 * 
 * This component ensures Safari mobile browser updates the website tinting color
 * immediately when navigating between pages, even with client-side navigation.
 * 
 * Safari can be slow to update theme-color from viewport metadata during client-side
 * navigation, so we manually update the meta tag on route changes.
 */
export default function ThemeColorUpdater() {
  const pathname = usePathname();

  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Get the theme color for the current route
      const themeColor = getPageBackgroundColor(pathname);

      // Find or create the theme-color meta tag
      // Safari looks for meta[name="theme-color"] to update the browser chrome color
      let metaThemeColor = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
      
      if (!metaThemeColor) {
        // Create the meta tag if it doesn't exist
        metaThemeColor = document.createElement('meta');
        metaThemeColor.setAttribute('name', 'theme-color');
        // Insert at the beginning of head for better Safari recognition
        document.head.insertBefore(metaThemeColor, document.head.firstChild);
      }

      // Update the content attribute with the new color
      // This triggers Safari to immediately update the browser chrome color
      metaThemeColor.setAttribute('content', themeColor);
    });
  }, [pathname]);

  // This component doesn't render anything
  return null;
}

